import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowRight } from "lucide-react";

export interface ContactStepProps {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  insurance: string;
  consent: boolean;
  submitting: boolean;
  contactValid: boolean;
  onFirstName: (v: string) => void;
  onLastName: (v: string) => void;
  onEmail: (v: string) => void;
  onPhone: (v: string) => void;
  onInsurance: (v: string) => void;
  onConsent: (v: boolean) => void;
  onSubmit: () => void;
  helpText?: string;
}

export function ContactStep({
  firstName,
  lastName,
  email,
  phone,
  insurance,
  consent,
  submitting,
  contactValid,
  onFirstName,
  onLastName,
  onEmail,
  onPhone,
  onInsurance,
  onConsent,
  onSubmit,
  helpText,
}: ContactStepProps) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="space-y-4"
      noValidate
    >
      <h2 className="font-headings text-lg font-bold text-primary sm:text-xl">
        Where can we reach you?
      </h2>
      {helpText && <p className="text-sm text-muted-foreground">{helpText}</p>}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label
            htmlFor="cs-first"
            className="text-sm font-semibold text-primary"
          >
            First Name *
          </Label>
          <Input
            id="cs-first"
            value={firstName}
            onChange={(e) => onFirstName(e.target.value)}
            placeholder="Jane"
            required
            disabled={submitting}
          />
        </div>
        <div className="space-y-1.5">
          <Label
            htmlFor="cs-last"
            className="text-sm font-semibold text-primary"
          >
            Last Name *
          </Label>
          <Input
            id="cs-last"
            value={lastName}
            onChange={(e) => onLastName(e.target.value)}
            placeholder="Doe"
            required
            disabled={submitting}
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label
          htmlFor="cs-email"
          className="text-sm font-semibold text-primary"
        >
          Email *
        </Label>
        <Input
          id="cs-email"
          type="email"
          value={email}
          onChange={(e) => onEmail(e.target.value)}
          placeholder="you@example.com"
          required
          disabled={submitting}
        />
      </div>
      <div className="space-y-1.5">
        <Label
          htmlFor="cs-phone"
          className="text-sm font-semibold text-primary"
        >
          Mobile Phone *
        </Label>
        <Input
          id="cs-phone"
          type="tel"
          inputMode="tel"
          value={phone}
          onChange={(e) => onPhone(e.target.value)}
          placeholder="(954) 555-1234"
          required
          disabled={submitting}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="cs-ins" className="text-sm font-semibold text-primary">
          Insurance carrier{" "}
          <span className="text-muted-foreground">(optional)</span>
        </Label>
        <Input
          id="cs-ins"
          value={insurance}
          onChange={(e) => onInsurance(e.target.value)}
          placeholder="e.g. Blue Cross Blue Shield"
          disabled={submitting}
        />
      </div>
      <label className="flex items-start gap-3 rounded-xl border border-border p-4">
        <Checkbox
          checked={consent}
          onCheckedChange={(v) => onConsent(v === true)}
        />
        <span className="text-xs text-muted-foreground leading-relaxed">
          I agree to be contacted by phone, text, or email about treatment
          options. Message and data rates may apply. See our{" "}
          <a
            href="/privacy-policy"
            className="text-accent underline underline-offset-2"
          >
            privacy policy
          </a>
          .
        </span>
      </label>
      <Button
        type="submit"
        disabled={!contactValid || submitting}
        size="lg"
        className="w-full bg-gradient-accent font-bold text-accent-foreground shadow-glow hover:opacity-90 disabled:opacity-50"
      >
        {submitting ? "Sending…" : "Check My Eligibility"}
        {!submitting && <ArrowRight className="ml-1 h-4 w-4" />}
      </Button>
    </form>
  );
}
